import {User} from '../../features/auth/models/User';
import {DASHBOARD_BLOCKED_ROLES, UserRole} from '../../constants/roles';

export interface AuthorizationResult {
  allowed: boolean;
  reason?: string;
  redirectTo?: string;
}

export class AuthorizationService {
  private static instance: AuthorizationService;

  private constructor () {}

  public static getInstance (): AuthorizationService {
    if (!AuthorizationService.instance) {
      AuthorizationService.instance = new AuthorizationService();
    }
    return AuthorizationService.instance;
  }

  /**
   * فحص ما إذا كان المستخدم مسموح له بالوصول للوحة التحكم
   */
  public canAccessDashboard (user: User | null): AuthorizationResult {
    if (!user) {
      return {
        allowed: false,
        reason: 'User not authenticated',
        redirectTo: '/login'
      };
    }

    // فحص الأدوار الممنوعة
    if (DASHBOARD_BLOCKED_ROLES.includes(user.role.name as UserRole)) {
      return {
        allowed: false,
        reason: `Role ${user.role.name} is not allowed to access dashboard`,
        redirectTo: '/unauthorized'
      };
    }

    return {allowed: true};
  }

  /**
   * فحص صلاحية محددة للمستخدم
   */
  public hasPermission (user: User | null, permission: string): boolean {
    if (!user) {
      return false;
    }

    // Use permissions from backend only (fully dynamic)
    if (user.permissions && user.permissions.length > 0) {
      // Check if user has the hasPermission method (class instance)
      if (typeof user.hasPermission === 'function') {
        return user.hasPermission(permission);
      } else {
        // Fallback for plain objects (from localStorage)
        return user.permissions.includes(permission);
      }
    }

    // No hardcoded fallback - permissions must come from API
    return false;
  }

  /**
   * فحص أي من الصلاحيات المحددة
   */
  public hasAnyPermission (user: User | null, permissions: string[]): boolean {
    if (!user) {
      return false;
    }


    // Use permissions from backend only (fully dynamic)
    if (user.permissions && user.permissions.length > 0) {
      // Check if user has the hasAnyPermission method (class instance)
      if (typeof user.hasAnyPermission === 'function') {
        const hasAnyPermission = user.hasAnyPermission(permissions);
        return hasAnyPermission;
      } else {
        // Fallback for plain objects (from localStorage)
        const hasAnyPermission = permissions.some(permission => user.permissions.includes(permission));
        return hasAnyPermission;
      }
    }

    // No hardcoded fallback - permissions must come from API
    return false;
  }

  /**
   * فحص دور محدد
   */
  public hasRole (user: User | null, requiredRole: string): boolean {
    if (!user) {return false;}
    return user.role.name === requiredRole;
  }

  /**
   * Check if user is a service provider
   */
  public isServiceProvider (user: User | null): boolean {
    if (!user) {return false;}
    const roleName = user.role?.name ? user.role.name.toLowerCase() : '';
    const username = user.username ? user.username.toLowerCase() : '';
    if (!roleName && !username) {return false;}

    const providerRoleNames = new Set<string>([
      UserRole.PROVIDER,
      'service_provider',
      'service-provider',
      'serviceprovider'
    ]);

    const isProviderByRole = roleName ? providerRoleNames.has(roleName) : false;
    const isProviderByUsername = username.includes('_provider') || username.includes('provider_');

    return isProviderByRole || isProviderByUsername;
  }

  /**
   * الحصول على رسالة خطأ للمستخدم حسب دوره
   */
  public getUnauthorizedMessage (user: User | null): {
    title: string;
    message: string;
    isMarketer: boolean;
  } {
    const isMarketer = user?.role?.name === UserRole.MARKETER;

    if (isMarketer) {
      return {
        title: 'غير مخول للوصول',
        message: 'أنت مسجل كـ مطور أسواق. لوحة التحكم مخصصة للإدارة فقط.',
        isMarketer: true
      };
    }

    return {
      title: 'غير مخول للوصول',
      message: 'ليس لديك صلاحية الوصول لهذه الصفحة.',
      isMarketer: false
    };
  }

  /**
   * Check if user has any of the specified roles
   */
  public hasAnyRole (user: User | null, roles: string[]): boolean {
    if (!user) { return false; }
    return roles.includes(user.role.name);
  }

  /**
   * Check if user has all of the specified roles
   */
  public hasAllRoles (user: User | null, roles: string[]): boolean {
    if (!user) { return false; }
    return roles.every(role => user.role.name === role);
  }

  /**
   * Check if user can perform action on specific resource
   */
  public canPerformAction (user: User | null, action: string, resource: string): boolean {
    if (!user) { return false; }

    const permission = `${action}_${resource}`.toUpperCase();
    return this.hasPermission(user, permission);
  }

  /**
   * Get user's role level for hierarchy comparison
   */
  public getRoleLevel (user: User | null): number {
    if (!user) { return 0; }

    const roleLevels: Record<string, number> = {
      [UserRole.SUPER_ADMIN]: 100,
      [UserRole.ADMIN]: 90,
      [UserRole.MANAGER]: 80,
      [UserRole.ACCOUNTANT]: 70,
      [UserRole.MODERATOR]: 60,
      [UserRole.PROVIDER]: 50,
      [UserRole.STORE]: 40,
      [UserRole.MARKETER]: 30
    };

    return roleLevels[user.role.name] || 0;
  }

  /**
   * Check if user can manage another user based on role hierarchy
   */
  public canManageUser (manager: User | null, targetUser: User | null): boolean {
    if (!manager || !targetUser) { return false; }

    const managerLevel = this.getRoleLevel(manager);
    const targetLevel = this.getRoleLevel(targetUser);

    // Manager must have higher role level than target user
    return managerLevel > targetLevel;
  }

  /**
   * Check if user is admin
   */
  public isAdmin (user: User | null): boolean {
    if (!user) {
      return false;
    }
    return user.role.name === UserRole.ADMIN;
  }

  /**
   * Check if user has financial permissions
   */
  public hasFinancialPermissions (user: User | null): boolean {
    if (!user) { return false; }

    const financialPermissions = [
      'view_financial_reports',
      'manage_billing',
      'manage_payments'
    ];

    return this.hasAnyPermission(user, financialPermissions);
  }

  /**
   * Check if user has management permissions
   */
  public hasManagementPermissions (user: User | null): boolean {
    if (!user) { return false; }

    const managementPermissions = [
      'manage_users',
      'manage_marketers',
      'manage_service_providers',
      'manage_products',
      'manage_stores'
    ];

    return this.hasAnyPermission(user, managementPermissions);
  }
}
